/**
 * RBAC constants — single source of truth for the frontend.
 *
 * Mirrors the post-refactor model: 11 modules × 2 actions × 2 scopes = 44
 * permission rows (catalogued in `t_iam_permissions`), seeded by migration
 * `20260529_004_rbac_refactor_phase_1.sql` and trimmed by the 2026-06-09
 * cleanup migration which dropped `workspace_analytics` and
 * `operations_dashboard`.
 *
 * This file is a temporary shim — the AccessGates API in `use-access.ts` is
 * still organised around the old persona-named gates (`canAccessWorkspace`,
 * `canAdminClient`, etc.). Phase 3 will collapse those gates into a
 * module-driven `useAccess().can(module, action)` API.
 */

// ── Dimensions ──

export const RbacModule = {
  ChartQueue: 'chart_queue',
  History: 'history',
  Notes: 'notes',
  ChartsDashboard: 'charts_dashboard',
  OperationsDashboard: 'operations_dashboard',
  AnalyticsDashboard: 'analytics_dashboard',
  PromptConfig: 'prompt_config',
  ClientConfig: 'client_config',
  KnowledgeBase: 'knowledge_base',
  MandatoryFields: 'mandatory_fields',
  // Internal MCP access (migration 20260724_001): gates the MCP Playbook page and
  // sidebar entry. Held by super_admin + the mcp_editor allowlist role; the
  // MCP server itself enforces the same module on every tool call.
  Mcp: 'mcp',
  Clients: 'clients',
  Notifications: 'notifications',
  Announcements: 'announcements',
  AccessRequest: 'access_request',
  SystemAdmin: 'system_admin',
} as const

export const RbacAction = {
  Read: 'read',
  Edit: 'edit',
} as const

export const RbacScope = {
  Own: 'own',
  All: 'all',
} as const

export type ModuleKey = (typeof RbacModule)[keyof typeof RbacModule]
export type ActionKey = (typeof RbacAction)[keyof typeof RbacAction]
export type ScopeKey = (typeof RbacScope)[keyof typeof RbacScope]

// ── Helpers ──

/** Scope hierarchy: `all` satisfies `own` checks. */
const SCOPE_LEVEL: Record<string, number> = {
  own: 1,
  all: 2,
}

/** Build a permission string from typed dimensions. */
export function perm(module: ModuleKey, action: ActionKey, scope: ScopeKey): string {
  return `${module}:${action}:${scope}`
}

/**
 * Check if the user holds at least the requested permission level.
 * Higher scope satisfies lower-scope checks (`all` satisfies `own`).
 * Also: `edit` satisfies `read` for the same (module, scope) — if you can
 * edit, you can read.
 */
export function hasPerm(permissions: string[], module: ModuleKey, action: ActionKey, scope: ScopeKey): boolean {
  const requiredLevel = SCOPE_LEVEL[scope] ?? 0

  // Build the prefixes we'll accept. For action=read, also accept edit
  // (since edit > read for the same module/scope).
  const actionPrefixes: string[] = [`${module}:${action}:`]
  if (action === RbacAction.Read) {
    actionPrefixes.push(`${module}:${RbacAction.Edit}:`)
  }

  return permissions.some((p) => {
    const matchingPrefix = actionPrefixes.find((pref) => p.startsWith(pref))
    if (!matchingPrefix) return false
    const permScope = p.slice(matchingPrefix.length)
    return (SCOPE_LEVEL[permScope] ?? 0) >= requiredLevel
  })
}

/**
 * Convenience: does the user hold ANY permission with scope='all' on any
 * module? Used as the "cross-client visibility" gate.
 */
export function hasAnyAllScope(permissions: string[]): boolean {
  return permissions.some((p) => p.endsWith(':all'))
}

// ── Home route resolution ──

/**
 * Determine the landing page for a user given their permission set.
 * Priority: chart_queue → charts_dashboard → analytics dashboard →
 * system admin → clients → knowledge_base.
 *
 * `/notifications` is deliberately NOT a landing candidate — it is a drill-down
 * surface reached from the header bell, not a standalone home page (it isn't
 * even in the sidebar; see app/navigation.ts). A role whose only meaningful
 * grants are notifications + clients (e.g. Billing Admin) must land on
 * `/clients`, so the clients check sits above knowledge_base and notifications
 * is not checked at all.
 *
 * Pure function — safe to call outside React.
 */
export function resolveHomeRoute(permissions: string[]): string {
  const check = (m: ModuleKey, a: ActionKey, s: ScopeKey) => hasPerm(permissions, m, a, s)

  if (check(RbacModule.ChartQueue, RbacAction.Read, RbacScope.Own)) return '/workspace/queue'
  if (check(RbacModule.ChartsDashboard, RbacAction.Read, RbacScope.Own)) return '/client-admin/overview'
  if (check(RbacModule.AnalyticsDashboard, RbacAction.Read, RbacScope.Own)) return '/engine-admin/overview'
  if (check(RbacModule.SystemAdmin, RbacAction.Read, RbacScope.All)) return '/system-admin/users'
  if (check(RbacModule.Clients, RbacAction.Read, RbacScope.Own)) return '/clients'
  if (check(RbacModule.KnowledgeBase, RbacAction.Read, RbacScope.Own)) return '/kb'

  return '/workspace/queue'
}

/**
 * Human-friendly label for the home destination — used by 403 screens.
 */
export function homeRouteLabel(route: string): string {
  if (route.startsWith('/workspace')) return 'Workspace'
  if (route.startsWith('/client-admin')) return 'Charts Dashboard'
  if (route.startsWith('/engine-admin')) return 'Analytics Dashboard'
  if (route.startsWith('/system-admin')) return 'System Admin'
  if (route.startsWith('/clients')) return 'Clients'
  if (route.startsWith('/kb')) return 'Knowledge Base'
  if (route.startsWith('/notifications')) return 'Notifications'
  return 'Home'
}
